import { ValidationSchema, validators } from 'views/declarations/uk/export/validations/validations';
import {
    activeBorderTransportMeans,
    additionalInformation,
    additionalSupplyChainActor,
    binaryIndicator,
    countriesOfRouting,
    customsOfficeOfFirstEntry,
    dateTime,
    goodsItemChildValidators,
    party,
    passiveBorderTransportMeans,
    place,
    referenceNumberUCR,
    representative,
    requiredLevel,
    requiredTransportDocument,
    supportingDocuments,
    transportCharges,
    transportEquipment,
} from './ics2EnsCommonValidation';

/** One house goods item — mirrors `GoodsItemType15`. F50 has no master item level. */
export const ics2EnsF50ValidationItem: ValidationSchema = {
    childValidators: {
        ...goodsItemChildValidators,
        passiveBorderTransportMeans: passiveBorderTransportMeans(),
    },
};

export const ics2EnsF50ValidationHouseConsignment: ValidationSchema = {
    childValidators: {
        containerIndicator: binaryIndicator(),
        totalGrossMass: [validators.required(), validators.float(16, 6)],
        referenceNumberUCR,
        // Both parties are mandatory once the goods shipment is there, so neither is looked at until
        // one of them has been started on - `stopAtEmpty` because an untouched pair is an object of
        // nulls rather than nothing at all. Whether the class is needed in the first place is C3020.
        goodsShipment: {
            childValidators: {
                buyer: party({ required: true }),
                seller: party({ required: true }),
            },
            stopAtEmpty: true,
        },
        placeOfAcceptance: place({ required: true }),
        placeOfDelivery: place({ required: true }),
        transportCharges: transportCharges({ required: true }),
        transportDocumentHouseLevel: requiredTransportDocument,
        passiveBorderTransportMeans: passiveBorderTransportMeans(),
        countriesOfRoutingOfConsignment: countriesOfRouting(),
        transportEquipment,
        supportingDocuments,
        additionalInformation,
        additionalSupplyChainActor,
    },
};

/**
 * The master level, plus the house consignments and their goods items. The house consignment and
 * goods item schemas are reused as the per-entry validators of their arrays, so a single run over
 * the master form values reports the errors of all three levels.
 */
export const ics2EnsF50ValidationHeader: ValidationSchema = {
    childValidators: {
        lrn: [validators.maxLength(22)],
        specificCircumstanceIndicator: [validators.required(), validators.exact(3)],
        reEntryIndicator: binaryIndicator(),
        documentIssueDate: dateTime(),
        customsOfficeOfFirstEntry,
        declarant: party({ required: true, eoriRequired: true, communicationRequired: true }),
        representative,
        activeBorderTransportMeans: activeBorderTransportMeans(),
        consignmentMasterLevel: {
            childValidators: {
                referenceNumberUCR,
                consignor: party({ required: true }),
                consignee: party({ required: true }),
                carrier: party({ required: true, eoriRequired: true }),
                placeOfAcceptance: place({ required: true }),
                placeOfLoading: place({ required: true }),
                placeOfUnloading: place({ required: true }),
                placeOfDelivery: place({ required: true }),
                transportCharges: transportCharges({ required: true }),
                transportDocumentMasterLevel: requiredTransportDocument,
                supportingDocuments,
                additionalSupplyChainActor,
                consignmentHouseLevel: requiredLevel({
                    ...ics2EnsF50ValidationHouseConsignment,
                    childValidators: {
                        ...ics2EnsF50ValidationHouseConsignment.childValidators,
                        goodsItem: requiredLevel(ics2EnsF50ValidationItem),
                    },
                }),
            },
        },
    },
};
